import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import PropertyPredictionCard from '../components/PropertyPredictionCard'
import LoadingSpinner from '../components/LoadingSpinner'
import { InlineSpinner } from '../components/LoadingSpinner'
import { getCities, getCityPredictions, getCityAccuracy, getLandingPreviewPredictions, autocomplete, searchPredictionsByAddress } from '../services/api'
import { PAUSE_SUBSCRIPTIONS, PAUSE_SINGLE_REPORT } from '../config'

/**
 * Landing Page - Predictions-first homepage showcasing data analysis SaaS
 */
export default function LandingPage() {
  const [citySearch, setCitySearch] = useState('')
  const [availableCities, setAvailableCities] = useState([])
  const [featuredCities, setFeaturedCities] = useState([])
  const [previewPredictions, setPreviewPredictions] = useState([])
  const [loadingCities, setLoadingCities] = useState(true)
  const [loadingPredictions, setLoadingPredictions] = useState(true)
  const [marketInsightsStats, setMarketInsightsStats] = useState([])
  const [loadingMarketInsights, setLoadingMarketInsights] = useState(false)
  const [showCityDropdown, setShowCityDropdown] = useState(false)
  const [filteredCities, setFilteredCities] = useState([])
  const [addressSuggestions, setAddressSuggestions] = useState([])
  const [loadingAddressSuggestions, setLoadingAddressSuggestions] = useState(false)
  const [navigating, setNavigating] = useState(false)
  const citySearchRef = useRef(null)
  const addressSearchDebounceRef = useRef(null)
  const navigate = useNavigate()
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Side Street',
    url: 'https://sidestreet.io',
    logo: 'https://sidestreet.io/images/new_logo_2.svg',
    sameAs: ['https://sidestreet.featurebase.app'],
  }
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Side Street',
    url: 'https://sidestreet.io',
    description: 'AI-powered Canadian real estate predictions, city stats, and market insights.',
  }

  // Metro area definitions - cities that belong to combined datasets
  // Note: Kawartha Lakes is a single municipality (uses Wards as neighborhoods), not a metro area
  const METRO_AREAS = {
    'Toronto': [
      'Toronto', 'Etobicoke', 'Scarborough', 'York', 'North York',
      'Brampton', 'Ajax', 'Mississauga', 'Pickering', 'Oshawa', 'Whitby',
      'Burlington', 'Milton', 'Oakville', 'Richmond Hill',
      'Hamilton', 'St Catharines', 'Brantford', 'Markham', 'Vaughan'
    ],
    'Vancouver': [
      'Vancouver', 'Burnaby', 'Richmond', 'Surrey', 'Coquitlam',
      'North Vancouver', 'West Vancouver',
      'Delta', 'Langley', 'Maple Ridge', 'Abbotsford', 'Chilliwack'
    ],
    'Kitchener': ['Kitchener', 'Waterloo', 'Cambridge', 'Guelph']
  }

  // Metro area display names with city counts
  const METRO_DISPLAY_NAMES = {
    'Toronto': `Toronto & Hamilton Area (${METRO_AREAS['Toronto'].length} cities)`,
    'Vancouver': `Vancouver Area (${METRO_AREAS['Vancouver'].length} cities)`,
    'Kitchener': `Kitchener-Waterloo Area (${METRO_AREAS['Kitchener'].length} cities)`
  }

  // City card background images (skyline / cityscape). Toronto–Calgary: Google; Ottawa/Edmonton/Winnipeg: Wikimedia Commons (CC/free use).
  const CITY_IMAGES = {
    'Toronto': 'https://lh3.googleusercontent.com/aida-public/AB6AXuBiFXBwTbECV4bHYBd6CUhc98Fp3xLw2lnFBsM6QAAaGIuj_xUe2tNM2Km9XX1R2WEfObLVHnyfWTAgw7mq1zygN0YyuWC9MDceQSaCPbh8KLpH2KjOHMRadQn1i38_Qt-YkR29xxotfZnwATL047bQeKXHKc_QS46c2gSYOKlEqHO9m7EH37JIZvTbPVxB3pgZsO4RXaEBe7AZiUA8NPwruGdBzIhX8mROSbfE_xBBpt4_WGuYeQb9rCYrX7tr6K3zU3b3LUtTPyaX',
    'Vancouver': 'https://lh3.googleusercontent.com/aida-public/AB6AXuDcTzheex4nn5XGbCXbXSYP0WwuRi2XTvpW4MmL6pSvh-5vaJcY5Hp0o4br63kUgtfK6k4wcjFNeGV2nXvUKa52MfumQeN5qci0svQ7ChrYi4X2J44zq00HA1I8r6ncCowO8PMPDOu-_p2i3Gq4KcaQKdm9hds7_LtMgS7rBOJO7WD7tjXCR7J0sUHyLXc8qKQTZVqxMBObwjD4wubLPID9CP6tJ46ISfO3oliKjDTyvs0OO7VTJubZznd9OhGsFflJmAuDgHuPIXWo',
    'Montreal': 'https://lh3.googleusercontent.com/aida-public/AB6AXuA_eXovB5J8eor7KeBbmsvqnfDqc7br_nmxJ8V0skBQ4-Pl4C4JWe_9-bhqI5WRrn-nKoLDIpMpUQI7KsheR5CcD7MUYXjvTtyTg6q4RbnnMHgc1OyzNhmVcaLbbLWfV7XnKG3gAKHidsZUB4nFapdQU4tN5RRtHdimIBJSR2bVC-UcmIKg-FNWVwfL9Q-krvjbE2O6lIWp4RuabvSQZQKhigHLGfEn7I5Q2rmDAIoXCvO1Kg5WHRscCj1eh5i_BLK4x0uq1X9Bt5CI',
    'Calgary': 'https://lh3.googleusercontent.com/aida-public/AB6AXuDemmlBpCBzzVFFt2eYAAE7jxlXXm-gIFHg6VPMEJnswArv2nuhTXSCYlvUKAizzD4-1EmGP4Qi2GHF6_Ga5Nr1VGIVsxDEZbTR_gd6Ttmygd-dAunL9pKE7adHEUGgg2_NIFTxyOJgvn3Wez7SUFUjK8BJKPBzV0u73dpjPNLYL4Hjxx4uVVIu7w2reo9al1mOU3ZaA9UbOUdLfQeUXzkL3ZCay2XyGguuF5CL6bIEFHMh4PNSBcJjHdpeRB86vCgIEFLisHCwQgX-',
    'Ottawa': '/images/ottawa-skyline.jpg',
    'Edmonton': '/images/edmonton.jpg',
    'Winnipeg': '/images/winnipeg.jpg'
  }
  const FALLBACK_CITY_IMAGE = CITY_IMAGES['Toronto']

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (citySearchRef.current && !citySearchRef.current.contains(event.target)) {
        setShowCityDropdown(false)
      }
    }

    if (showCityDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showCityDropdown])

  // Fetch available cities on mount
  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoadingCities(true)
        const cities = await getCities()
        setAvailableCities(cities)
        
        // Select 6 featured cities (prioritize major ones)
        const majorCities = ['Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Ottawa', 'Edmonton', 'Winnipeg', 'Quebec City', 'Hamilton']
        const featured = majorCities
          .filter(city => cities.some(c => c.city === city))
          .slice(0, 6)
          .map(cityName => {
            const cityData = cities.find(c => c.city === cityName)
            return cityData || { city: cityName }
          })
        
        // If we don't have 6 major cities, fill with first available
        if (featured.length < 6) {
          const remaining = cities
            .filter(c => !featured.some(f => f.city === c.city))
            .slice(0, 6 - featured.length)
          featured.push(...remaining)
        }
        
        setFeaturedCities(featured.slice(0, 6))
      } catch (err) {
        console.error('Error fetching cities:', err)
      } finally {
        setLoadingCities(false)
      }
    }

    fetchCities()
  }, [])

  // Fetch accuracy stats for featured cities (Market Insights section)
  useEffect(() => {
    if (featuredCities.length === 0) return
    const fetchMarketInsights = async () => {
      setLoadingMarketInsights(true)
      try {
        const results = await Promise.allSettled(
          featuredCities.map(c => getCityAccuracy(c.city, 'house'))
        )
        const stats = featuredCities.map((c, i) => ({
          city: c.city,
          displayName: c.city,
          houseMetrics: results[i].status === 'fulfilled' && results[i].value?.city_metrics
            ? results[i].value.city_metrics
            : null
        }))
        setMarketInsightsStats(stats)
      } catch (err) {
        console.error('Error fetching market insights:', err)
      } finally {
        setLoadingMarketInsights(false)
      }
    }
    fetchMarketInsights()
  }, [featuredCities])

  // Fetch preview predictions: single API call returns 6 items (4 high, 1 med, 1 low, city variety)
  useEffect(() => {
    let cancelled = false
    const fetchPreviewPredictions = async () => {
      try {
        setLoadingPredictions(true)
        const data = await getLandingPreviewPredictions()
        if (!cancelled) {
          setPreviewPredictions(data.predictions || [])
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching preview predictions:', err)
        }
      } finally {
        if (!cancelled) {
          setLoadingPredictions(false)
        }
      }
    }
    fetchPreviewPredictions()
    return () => { cancelled = true }
  }, [])

  // Filter and deduplicate cities based on search input
  useEffect(() => {
    if (availableCities.length === 0) {
      setFilteredCities([])
      return
    }

    // Get metro keys for filtering
    const metroKeys = new Set(Object.keys(METRO_AREAS).map(k => k.toLowerCase()))
    const metroCities = new Set()
    Object.values(METRO_AREAS).forEach(cityList => {
      cityList.forEach(city => metroCities.add(city.toLowerCase()))
    })

    // Group cities by name and collect property types
    const cityMap = new Map()
    availableCities.forEach(city => {
      const cityName = city.city?.trim()
      if (!cityName) return // Skip invalid cities
      
      // Skip "Combined" entries
      if (cityName.toLowerCase().includes('combined')) return
      
      if (!cityMap.has(cityName)) {
        cityMap.set(cityName, {
          city: cityName,
          property_types: new Set(),
          lat: city.lat,
          lon: city.lon
        })
      }
      // Add property type (normalize to lowercase for consistency)
      if (city.property_type) {
        cityMap.get(cityName).property_types.add(city.property_type.toLowerCase())
      }
    })

    // Convert to array with property type flags, excluding main metro keys
    let uniqueCities = Array.from(cityMap.values())
      .filter(city => !metroKeys.has(city.city.toLowerCase())) // Exclude Toronto, Vancouver, etc.
      .map(city => {
        const propertyTypesArray = Array.from(city.property_types)
        return {
          ...city,
          displayName: city.city, // Individual cities use their name as display
          property_types: propertyTypesArray,
          hasHouse: propertyTypesArray.includes('house'),
          hasCondo: propertyTypesArray.includes('condo'),
          isPartOfMetro: metroCities.has(city.city.toLowerCase())
        }
      })

    // Create metro area entries
    const metroEntries = Object.entries(METRO_AREAS).map(([metroKey, cityList]) => {
      const metroData = cityMap.get(metroKey) || {}
      return {
        city: metroKey,
        displayName: METRO_DISPLAY_NAMES[metroKey],
        isMetroArea: true,
        subCities: cityList,
        property_types: Array.from(metroData.property_types || []),
        hasHouse: true, // Metro areas have both
        hasCondo: true,
        lat: metroData.lat || 0,
        lon: metroData.lon || 0
      }
    })

    // Filter by search term
    const searchLower = citySearch.toLowerCase().trim()
    if (searchLower !== '') {
      // Filter metro entries - match on display name, city key, or any sub-city
      const filteredMetros = metroEntries.filter(metro =>
        metro.displayName.toLowerCase().includes(searchLower) ||
        metro.city.toLowerCase().includes(searchLower) ||
        metro.subCities.some(sub => sub.toLowerCase().includes(searchLower))
      )
      
      // Filter individual cities
      uniqueCities = uniqueCities.filter(city =>
        city.city.toLowerCase().includes(searchLower)
      )
      
      // Combine: matched metros first, then matched individual cities
      uniqueCities = [...filteredMetros, ...uniqueCities]
    } else {
      // No search - show metros first, then all cities
      uniqueCities = [...metroEntries, ...uniqueCities]
    }

    // Sort individual cities: cities with both types first, then alphabetically
    // But keep metro areas at the top
    uniqueCities.sort((a, b) => {
      // Metro areas always come first
      if (a.isMetroArea && !b.isMetroArea) return -1
      if (!a.isMetroArea && b.isMetroArea) return 1
      
      // Among non-metros, sort by property types then alphabetically
      if (!a.isMetroArea && !b.isMetroArea) {
        const aBoth = a.hasHouse && a.hasCondo
        const bBoth = b.hasHouse && b.hasCondo
        if (aBoth !== bBoth) return bBoth ? 1 : -1
        return a.city.localeCompare(b.city)
      }
      
      return 0
    })

    // Show all cities (dropdown has max-height and scroll)
    setFilteredCities(uniqueCities)
  }, [citySearch, availableCities])

  // Address autocomplete via geocoding API (same as predict page) for address-like queries.
  useEffect(() => {
    if (addressSearchDebounceRef.current) {
      clearTimeout(addressSearchDebounceRef.current)
      addressSearchDebounceRef.current = null
    }

    const query = citySearch.trim()
    const queryLower = query.toLowerCase()
    const looksLikeAddress =
      /^\d+[a-zA-Z0-9-]*\s+/.test(query) ||
      /\b(st|street|ave|avenue|rd|road|dr|drive|blvd|boulevard|ln|lane|ct|court|cres|crescent|way|trl|trail|terr|terrace|pl|place|hwy|highway)\b/i.test(queryLower)

    if (!showCityDropdown || query.length < 3 || !looksLikeAddress) {
      setAddressSuggestions([])
      setLoadingAddressSuggestions(false)
      return
    }

    let cancelled = false
    setLoadingAddressSuggestions(true)
    addressSearchDebounceRef.current = setTimeout(async () => {
      try {
        const data = await autocomplete(query, 6)
        if (cancelled) return
        const features = (data?.features || []).filter(f => f?.properties?.name)
        setAddressSuggestions(features)
      } catch {
        if (!cancelled) setAddressSuggestions([])
      } finally {
        if (!cancelled) setLoadingAddressSuggestions(false)
      }
    }, 280)

    return () => {
      cancelled = true
      if (addressSearchDebounceRef.current) {
        clearTimeout(addressSearchDebounceRef.current)
        addressSearchDebounceRef.current = null
      }
    }
  }, [citySearch, showCityDropdown])

  const handleCitySearch = async (e) => {
    e.preventDefault()
    const searchTerm = citySearch.trim()
    if (!searchTerm || navigating) return

    const searchTermLower = searchTerm.toLowerCase()
    const exactCity = filteredCities.find(c => c.city?.toLowerCase() === searchTermLower)
    if (exactCity) {
      navigate(`/browse?city=${encodeURIComponent(exactCity.city)}`)
      return
    }

    const looksLikeAddress =
      /^\d+[a-zA-Z0-9-]*\s+/.test(searchTerm) ||
      /\b(st|street|ave|avenue|rd|road|dr|drive|blvd|boulevard|ln|lane|ct|court|cres|crescent|way|trl|trail|terr|terrace|pl|place|hwy|highway)\b/i.test(searchTermLower)

    if (looksLikeAddress) {
      setNavigating(true)
      try {
        const precomputed = await searchPredictionsByAddress(searchTerm, null, 1)
        const best = precomputed?.matches?.[0]
        const matchedCity = best?.city
        if (matchedCity) {
          const matchedCityKey =
            availableCities.find(c => c.city?.toLowerCase() === String(matchedCity).toLowerCase())?.city ||
            matchedCity
          navigate(`/browse?city=${encodeURIComponent(matchedCityKey)}&address=${encodeURIComponent(searchTerm)}`)
          return
        }
      } catch {
        // If lookup fails, continue with custom predict fallback.
      } finally {
        setNavigating(false)
      }

      navigate(`/predict?address=${encodeURIComponent(searchTerm)}`)
      return
    }

    navigate(`/browse?city=${encodeURIComponent(searchTerm)}`)
  }

  const handleCityClick = (cityName) => {
    navigate(`/browse?city=${encodeURIComponent(cityName)}`)
  }

  const handleCitySelect = (cityName) => {
    setCitySearch(cityName)
    setShowCityDropdown(false)
    navigate(`/browse?city=${encodeURIComponent(cityName)}`)
  }

  const handleAddressSelect = async (feature) => {
    const props = feature?.properties
    if (!props?.name || navigating) return
    const addressName = props.name
    const locality = props.locality || ''
    const label = props.label || addressName
    setCitySearch(label)
    setShowCityDropdown(false)

    const matchedCityObj = locality
      ? availableCities.find(c => c.city?.toLowerCase() === locality.toLowerCase())
      : null

    if (matchedCityObj) {
      setNavigating(true)
      try {
        const precomputed = await searchPredictionsByAddress(addressName, matchedCityObj.city, 1)
        if (precomputed?.matches?.[0]) {
          navigate(`/browse?city=${encodeURIComponent(matchedCityObj.city)}&address=${encodeURIComponent(addressName)}`)
          return
        }
      } catch { /* fall through */ } finally {
        setNavigating(false)
      }
      navigate(`/predict?address=${encodeURIComponent(label)}`)
    } else {
      navigate(`/predict?address=${encodeURIComponent(label)}`)
    }
  }

  const handlePredictionClick = (prediction) => {
    // Store prediction and navigate to details
    const propertyData = {
      address: prediction.address,
      city: prediction.city,
      propertyType: prediction.property_type,
      coordinates: {
        lat: prediction.lat,
        lon: prediction.lon
      },
      features: {
        neighbourhood: prediction.neighbourhood,
        bedrooms_total: prediction.bedrooms_total,
        bathrooms_total: prediction.bathrooms_total,
        living_area: prediction.living_area,
        lat: prediction.lat,
        lon: prediction.lon,
        lot_size: prediction.lot_size,
        has_garage: prediction.has_garage,
        has_basement: prediction.has_basement,
        basement_finished: prediction.basement_finished,
        basement_unfinished: prediction.basement_unfinished,
        overall_condition: prediction.overall_condition,
        house_style: prediction.house_style
      },
      prediction: {
        predicted_price: prediction.predicted_price,
        confidence_lower: prediction.confidence_lower,
        confidence_upper: prediction.confidence_upper,
        city: prediction.city,
        property_type: prediction.property_type,
        model_version: prediction.model_version,
        estimated_features: prediction.estimated_features,
        // Confidence metrics
        confidence_tier: prediction.confidence_tier,
        neighborhood_confidence_tier: prediction.neighborhood_confidence_tier,
        composite_confidence: prediction.composite_confidence,
        ci_width_pct: prediction.ci_width_pct,
        pi_width_pct: prediction.pi_width_pct,  // PI ±X% display
        neighborhood_median_error: prediction.neighborhood_median_error,
        // Confidence Interval bounds (for distribution visualization)
        ci_90_lower: prediction.ci_90_lower,
        ci_90_upper: prediction.ci_90_upper,
        ci_95_lower: prediction.ci_95_lower,
        ci_95_upper: prediction.ci_95_upper,
        ci_99_lower: prediction.ci_99_lower,
        ci_99_upper: prediction.ci_99_upper,
        // Prediction Interval bounds
        pi_90_lower: prediction.pi_90_lower,
        pi_90_upper: prediction.pi_90_upper,
        pi_95_lower: prediction.pi_95_lower,
        pi_95_upper: prediction.pi_95_upper,
        pi_99_lower: prediction.pi_99_lower,
        pi_99_upper: prediction.pi_99_upper,
        dom_predicted: prediction.dom_predicted,
        dom_pi_lower: prediction.dom_pi_lower,
        dom_pi_upper: prediction.dom_pi_upper,
        dom_90_lower: prediction.dom_90_lower,
        dom_90_upper: prediction.dom_90_upper,
        dom_std: prediction.dom_std,
        // Relist tracking
        is_relisted: prediction.is_relisted,
        relist_date: prediction.relist_date,
        relist_price: prediction.relist_price,
        list_date: prediction.list_date,
        // Include all fields that might be needed for display
        neighbourhood: prediction.neighbourhood,
        bedrooms_total: prediction.bedrooms_total,
        bathrooms_total: prediction.bathrooms_total,
        living_area: prediction.living_area,
        lot_size: prediction.lot_size,
        has_garage: prediction.has_garage,
        has_basement: prediction.has_basement,
        basement_finished: prediction.basement_finished,
        basement_unfinished: prediction.basement_unfinished,
        overall_condition: prediction.overall_condition,
        house_style: prediction.house_style,
        property_id: prediction.property_id
      }
    }
    sessionStorage.setItem('propertyData', JSON.stringify(propertyData))
    const pid = prediction.property_id
    navigate(pid ? `/property/${pid}` : '/property')
  }

  return (
    <div className="w-full">
      <Helmet>
        <title>Canadian Real Estate AI Predictions | Side Street</title>
        <meta
          name="description"
          content="Explore AI-powered property predictions, market insights, and city-level trends across 50+ Canadian real estate markets."
        />
        <meta property="og:title" content="Canadian Real Estate AI Predictions | Side Street" />
        <meta
          property="og:description"
          content="Explore AI-powered property predictions, market insights, and city-level trends across 50+ Canadian real estate markets."
        />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      </Helmet>
      {/* Hero Section - Background image + gradient overlay */}
      <section className="relative w-full py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-20">
        <div className="max-w-[1200px] mx-auto">
          <div
            className="relative overflow-visible rounded-2xl md:rounded-3xl shadow-2xl flex min-h-[480px] sm:min-h-[520px] md:min-h-[560px] flex-col gap-6 sm:gap-8 bg-cover bg-center bg-no-repeat items-center justify-center p-8 md:p-16 text-center"
            style={{
              backgroundImage: `linear-gradient(rgba(13, 27, 42, 0.72) 0%, rgba(13, 27, 42, 0.88) 100%), url("https://lh3.googleusercontent.com/aida-public/AB6AXuAdqr2B5BHXydlV540LFbQJ2pMc_ufQNcJAnn51_I6wYrG8s3ZK_Drg0eJ1tSmJgTeibXf-VVB4C3DU9X5Ubr8ZBcIHUhpY4tyxB7Epen2GmStSvCsK50UWUKjppmpmpcdL5ciZJQJhRgWgiBFtynDY-VSVZAJtBZa-YqDsg0T4cHtm0dSC2WI6HSgekSy3k1Mw3cnrem_wpbS3s3UTl9_mwFWAwnZSJh2Kun_xzhMEwVuvUtBRCW3oVJ9gtggkcFoChqsi9hqml-AE")`
            }}
          >
            <div className="flex flex-col gap-4 max-w-3xl">
              <span className="inline-block mx-auto px-3 sm:px-4 py-1 rounded-full bg-accent-gold/20 text-accent-gold text-xs font-bold uppercase tracking-widest border border-accent-gold/30">
                DATA-DRIVEN, The numbers behind the sold price
              </span>
              <h1 className="text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-tight tracking-tight">
                The Future of Canadian Real Estate,{' '}
                <span className="text-primary italic">Predicted.</span>
              </h1>
              <p className="text-slate-200 text-xl sm:text-2xl font-semibold max-w-2xl mx-auto leading-snug">
                Explore active listing predictions across 50+ Canadian markets
              </p>
              <p className="text-slate-300 text-lg sm:text-xl font-medium max-w-2xl mx-auto leading-relaxed">
                Real-time AI-powered market intelligence. Browse predictions or get custom analysis for any property.
              </p>
            </div>

            {/* City Search Bar */}
            <form onSubmit={handleCitySearch} className="w-full max-w-2xl">
              <div className="relative" ref={citySearchRef}>
                <div className="flex flex-col sm:flex-row gap-3 bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-2">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={citySearch}
                      onChange={(e) => {
                        setCitySearch(e.target.value)
                        setShowCityDropdown(true)
                      }}
                      onFocus={() => setShowCityDropdown(true)}
                      placeholder="Search by city or enter an address"
                      className="w-full px-4 py-3 sm:py-4 bg-transparent text-navy-deep dark:text-white placeholder-slate-400 text-center sm:text-left focus:outline-none text-base sm:text-lg"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={navigating}
                    className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-primary text-white rounded-lg sm:rounded-xl font-bold text-base sm:text-lg hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {navigating ? (
                      <>
                        <InlineSpinner size="sm" className="border-white" />
                        <span>Searching...</span>
                      </>
                    ) : (
                      'Browse Predictions'
                    )}
                  </button>
                </div>

                {/* Dropdown - Positioned relative to the entire search bar container */}
                {showCityDropdown && (filteredCities.length > 0 || addressSuggestions.length > 0 || loadingAddressSuggestions) && (
                  <div className="absolute z-50 w-full mt-2 bg-white dark:bg-slate-900 rounded-lg shadow-xl border border-slate-200 dark:border-slate-800 max-h-80 overflow-y-auto">
                    {loadingAddressSuggestions && (
                      <div className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800">
                        Searching addresses...
                      </div>
                    )}
                    {addressSuggestions.length > 0 && (
                      <>
                        <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                          Address suggestions
                        </div>
                        {addressSuggestions.map((feature, index) => {
                          const props = feature.properties || {}
                          const locality = props.locality || ''
                          const region = props.region_a || props.region || ''
                          const subtitle = [locality, region].filter(Boolean).join(', ')
                          return (
                            <button
                              key={props.gid || `addr-${index}`}
                              type="button"
                              onClick={() => handleAddressSelect(feature)}
                              className="w-full text-left px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800"
                            >
                              <div className="flex items-start gap-2 min-w-0">
                                <span className="material-symbols-outlined text-lg text-primary mt-0.5">location_on</span>
                                <div className="min-w-0">
                                  <div className="text-navy-deep dark:text-white font-semibold truncate">{props.name}</div>
                                  {subtitle && <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{subtitle}</div>}
                                </div>
                              </div>
                            </button>
                          )
                        })}
                      </>
                    )}
                    {filteredCities.map((city, index) => (
                      <button
                        key={`${city.city}-${index}`}
                        type="button"
                        onClick={() => handleCitySelect(city.city)}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800 last:border-b-0 ${
                          city.isMetroArea ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className={`material-symbols-outlined text-lg flex-shrink-0 ${city.isMetroArea ? 'text-blue-600' : 'text-primary'}`}>
                              {city.isMetroArea ? 'hub' : 'location_city'}
                            </span>
                            <span className="text-navy-deep dark:text-white font-semibold text-left whitespace-normal sm:whitespace-nowrap sm:truncate leading-tight">
                              {city.displayName || city.city}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {city.isMetroArea ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                                Metro Area
                              </span>
                            ) : (
                              <>
                                {city.hasHouse && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                                    House
                                  </span>
                                )}
                                {city.hasCondo && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                                    Condo
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </form>

            {/* Quick Stats */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm sm:text-base text-slate-200">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">home</span>
                <span className="font-semibold">Houses & Condos</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">public</span>
                <span className="font-semibold">{availableCities.length > 0 ? `${new Set(availableCities.map(c => c.city)).size} Cities` : 'Multiple Cities'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">update</span>
                <span className="font-semibold">Updated Weekly</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">bolt</span>
                <span className="font-semibold">+ Real-time Predictions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <div className="w-full bg-navy-deep dark:bg-slate-950 border-b border-white/5">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-20 py-3 sm:py-4">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 sm:gap-x-10">
            <div className="flex items-center gap-2 text-slate-300 text-xs sm:text-sm font-semibold">
              <span className="material-symbols-outlined text-primary text-base">verified</span>
              Predictions independent of any brokerage
            </div>
            <div className="hidden sm:block w-px h-4 bg-white/10" />
            <div className="flex items-center gap-2 text-slate-300 text-xs sm:text-sm font-semibold">
              <span className="material-symbols-outlined text-primary text-base">query_stats</span>
              Valuations never influenced by who&apos;s selling
            </div>
            <div className="hidden sm:block w-px h-4 bg-white/10" />
            <div className="flex items-center gap-2 text-slate-300 text-xs sm:text-sm font-semibold">
              <span className="material-symbols-outlined text-primary text-base">lock</span>
              No referral fees baked into our numbers
            </div>
          </div>
        </div>
      </div>


      {/* What You Can Do — Feature Overview */}
      <section className="px-4 sm:px-6 md:px-20 py-12 sm:py-16 bg-slate-50 dark:bg-slate-950">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-navy-deep dark:text-white text-3xl sm:text-4xl font-black mb-3">
              Everything You Need to Know the Market
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-lg max-w-2xl mx-auto">
              From browsing active listings to tracking your home's equity — Side Street puts data in your hands.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">

            {/* Browse Predictions — free */}
            <button
              onClick={() => navigate('/browse/')}
              className="group text-left flex flex-col gap-4 p-6 rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 hover:border-primary hover:shadow-lg transition-all duration-200"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <span className="material-symbols-outlined text-2xl text-primary">search</span>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-navy-deep dark:text-white text-lg font-black">Browse Predictions</h3>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">FREE</span>
                </div>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                  See AI-predicted values for every active listing in 50+ Canadian cities. Compare predictions to list prices and spot over- or under-priced properties.
                </p>
              </div>
              <span className="text-primary text-sm font-bold flex items-center gap-1 mt-auto group-hover:gap-2 transition-all">
                Browse now <span className="material-symbols-outlined text-base">arrow_forward</span>
              </span>
            </button>

            {/* My Home / Equity Tracking — free */}
            <button
              onClick={() => navigate('/my-home')}
              className="group text-left flex flex-col gap-4 p-6 rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 hover:border-emerald-500 hover:shadow-lg transition-all duration-200"
            >
              <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center group-hover:bg-emerald-200 dark:group-hover:bg-emerald-800/40 transition-colors">
                <span className="material-symbols-outlined text-2xl text-emerald-600 dark:text-emerald-400">home</span>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-navy-deep dark:text-white text-lg font-black">Home Equity Tracking</h3>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">FREE</span>
                </div>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                  Claim your home to get a free valuation and track how your equity changes over time. No credit card required.
                </p>
              </div>
              <span className="text-emerald-600 dark:text-emerald-400 text-sm font-bold flex items-center gap-1 mt-auto group-hover:gap-2 transition-all">
                Claim my home <span className="material-symbols-outlined text-base">arrow_forward</span>
              </span>
            </button>

            {/* City Stats — free */}
            <button
              onClick={() => navigate('/citystats/')}
              className="group text-left flex flex-col gap-4 p-6 rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 hover:border-primary hover:shadow-lg transition-all duration-200"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <span className="material-symbols-outlined text-2xl text-primary">bar_chart</span>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-navy-deep dark:text-white text-lg font-black">City Stats</h3>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">FREE</span>
                </div>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                  See model accuracy, median error, and price trends for each market. Know how reliable the predictions are in the city you're buying or selling in.
                </p>
              </div>
              <span className="text-primary text-sm font-bold flex items-center gap-1 mt-auto group-hover:gap-2 transition-all">
                View city stats <span className="material-symbols-outlined text-base">arrow_forward</span>
              </span>
            </button>

            {/* ROI Calculator */}
            <button
              onClick={() => navigate(PAUSE_SUBSCRIPTIONS ? '/roi-calculator/' : '/pricing/')}
              className="group text-left flex flex-col gap-4 p-6 rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 hover:border-accent-gold hover:shadow-lg transition-all duration-200"
            >
              <div className="w-12 h-12 rounded-xl bg-accent-gold/10 flex items-center justify-center group-hover:bg-accent-gold/20 transition-colors">
                <span className="material-symbols-outlined text-2xl text-accent-gold">calculate</span>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-navy-deep dark:text-white text-lg font-black">ROI Calculator</h3>
                  {PAUSE_SUBSCRIPTIONS ? (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">FREE</span>
                  ) : (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">HOME WEALTH+</span>
                  )}
                </div>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                  Model renovations before you commit. Adjust condition ratings, add upgrades, and see the estimated impact on your home's value with real construction cost benchmarks.
                </p>
              </div>
              <span className="text-accent-gold text-sm font-bold flex items-center gap-1 mt-auto group-hover:gap-2 transition-all">
                {PAUSE_SUBSCRIPTIONS ? 'Run an analysis' : 'See plans'} <span className="material-symbols-outlined text-base">arrow_forward</span>
              </span>
            </button>

            {/* Custom Predictions — Buyer/Pro */}
            <button
              onClick={() => navigate('/predict/')}
              className="group text-left flex flex-col gap-4 p-6 rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 hover:border-primary hover:shadow-lg transition-all duration-200"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <span className="material-symbols-outlined text-2xl text-primary">query_stats</span>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-navy-deep dark:text-white text-lg font-black">Custom Predictions</h3>
                  {PAUSE_SUBSCRIPTIONS ? (
                    PAUSE_SINGLE_REPORT ? (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">FREE</span>
                    ) : (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary/10 dark:bg-primary/20 text-primary border border-primary/30">CA$9.99</span>
                    )
                  ) : (
                    <>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary/10 dark:bg-primary/20 text-primary border border-primary/30">BUYER</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-700">PRO</span>
                    </>
                  )}
                </div>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                  Enter any address — even off-market or recently sold — and get an instant AI price estimate with adjusted comparables and a full confidence breakdown.
                </p>
              </div>
              <span className="text-primary text-sm font-bold flex items-center gap-1 mt-auto group-hover:gap-2 transition-all">
                Try a prediction <span className="material-symbols-outlined text-base">arrow_forward</span>
              </span>
            </button>

            {/* PDF Reports - hidden when both subs and single report are paused */}
            {!(PAUSE_SUBSCRIPTIONS && PAUSE_SINGLE_REPORT) && (
            <button
              onClick={() => navigate(PAUSE_SUBSCRIPTIONS ? '/predict/' : '/pricing/')}
              className="group text-left flex flex-col gap-4 p-6 rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 hover:border-primary hover:shadow-lg transition-all duration-200"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <span className="material-symbols-outlined text-2xl text-primary">picture_as_pdf</span>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-navy-deep dark:text-white text-lg font-black">PDF Property Reports</h3>
                  {PAUSE_SUBSCRIPTIONS ? (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary/10 dark:bg-primary/20 text-primary border border-primary/30">CA$9.99</span>
                  ) : (
                    <>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary/10 dark:bg-primary/20 text-primary border border-primary/30">BUYER</span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-700">PRO</span>
                    </>
                  )}
                </div>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                  Export a full DNA-level property report — prediction, confidence intervals, adjusted comps, and renovation ROI — as a shareable PDF to bring to negotiations.
                </p>
              </div>
              <span className="text-primary text-sm font-bold flex items-center gap-1 mt-auto group-hover:gap-2 transition-all">
                {PAUSE_SUBSCRIPTIONS ? 'Get a report' : 'View plans'} <span className="material-symbols-outlined text-base">arrow_forward</span>
              </span>
            </button>
            )}

          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-10 sm:mt-12">
            <button
              onClick={() => navigate(PAUSE_SUBSCRIPTIONS ? '/my-home/' : '/pricing/')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 text-base"
            >
              {PAUSE_SUBSCRIPTIONS ? 'Claim my home — free' : 'See all plans & pricing'}
              <span className="material-symbols-outlined">arrow_forward</span>
            </button>
            <p className="text-slate-400 dark:text-slate-500 text-sm mt-3">
              {PAUSE_SUBSCRIPTIONS
                ? 'Everything is free while we grow. No credit card required.'
                : 'Browse and equity tracking are free forever. No credit card required.'}
            </p>
          </div>
        </div>
      </section>

      {/* Featured Cities Section */}
      <section className="px-4 sm:px-6 md:px-20 py-12 sm:py-16 bg-white dark:bg-slate-900">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col gap-8">
            <div className="text-center">
              <h2 className="text-navy-deep dark:text-white text-3xl sm:text-4xl font-black mb-3">
                Explore Major Markets
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-lg">
                Browse predictions across Canada's largest real estate markets
              </p>
            </div>
            
            {loadingCities ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4 sm:gap-6">
                  {featuredCities.map((city) => {
                    const bgImage = CITY_IMAGES[city.city] || FALLBACK_CITY_IMAGE
                    return (
                      <button
                        key={city.city}
                        onClick={() => handleCityClick(city.city)}
                        className="group flex flex-col rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 text-left border border-slate-200 dark:border-slate-800"
                      >
                        <div className="relative h-48 sm:h-56 min-h-[12rem] overflow-hidden">
                          <div
                            className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                            style={{ backgroundImage: `url(${bgImage})` }}
                            aria-hidden
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/85 via-navy-deep/40 to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 p-4 flex flex-col gap-1">
                            <h3 className="text-white font-bold text-lg sm:text-xl drop-shadow-sm">
                              {city.city}
                            </h3>
                            <div className="flex items-center gap-2 text-sm text-white/90">
                              <span className="material-symbols-outlined text-base">location_city</span>
                              <span>View Predictions →</span>
                            </div>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
                <div className="text-center mt-6">
                  <button
                    onClick={() => navigate('/browse/')}
                    className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-semibold transition-colors"
                  >
                    View all {new Set(availableCities.map(c => c.city)).size} cities
                    <span className="material-symbols-outlined text-lg">arrow_forward</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Live Predictions Preview */}
      <section className="px-4 sm:px-6 md:px-20 py-12 sm:py-16 bg-slate-50 dark:bg-slate-950">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col gap-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-navy-deep dark:text-white text-3xl sm:text-4xl font-black mb-3">
                  Recent Predictions
                </h2>
                <p className="text-slate-600 dark:text-slate-400 text-lg">
                  Sample predictions from our database
                </p>
              </div>
              <button
                onClick={() => navigate('/browse/')}
                className="hidden sm:flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg font-bold hover:bg-primary/90 transition-all"
              >
                View All Predictions
                <span className="material-symbols-outlined">arrow_forward</span>
              </button>
            </div>

            {loadingPredictions ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : previewPredictions.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {previewPredictions.map((prediction, index) => (
                    <PropertyPredictionCard
                      key={index}
                      prediction={prediction}
                      onClick={() => handlePredictionClick(prediction)}
                      forceShowConfidenceBadge
                    />
                  ))}
                </div>
                <div className="flex justify-center sm:hidden">
                  <button
                    onClick={() => navigate('/browse/')}
                    className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg font-bold hover:bg-primary/90 transition-all"
                  >
                    View All Predictions
                    <span className="material-symbols-outlined">arrow_forward</span>
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                No predictions available at the moment.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Market Insights Section - Real accuracy by top 6 cities */}
      <section className="px-4 sm:px-6 md:px-20 py-12 sm:py-16 bg-white dark:bg-slate-900">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col gap-8">
            <div className="text-center">
              <h2 className="text-navy-deep dark:text-white text-3xl sm:text-4xl font-black mb-3">
                Market Insights
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-lg">
                Model accuracy by market — compare cities at a glance
              </p>
            </div>

            {loadingMarketInsights ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {marketInsightsStats.map((stat) => {
                    const m = stat.houseMetrics
                    // Prioritize training metrics (actual model accuracy vs sale prices)
                    const hasTrainingMetrics = m?.training_r2 != null || m?.training_mape != null
                    
                    return (
                      <div
                        key={stat.city}
                        className="flex flex-col gap-3 rounded-xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm"
                      >
                        <div className="flex items-center gap-2 text-primary">
                          <span className="material-symbols-outlined">verified</span>
                          <p className="text-slate-500 dark:text-slate-400 text-sm font-bold uppercase tracking-wider">
                            {stat.displayName || stat.city}
                          </p>
                        </div>
                        {m ? (
                          hasTrainingMetrics ? (
                            // Show training metrics (R², MAPE) - actual model accuracy
                            <div className="flex flex-col gap-1">
                              <p className="text-navy-deep dark:text-white text-2xl sm:text-3xl font-black">
                                {m.training_r2 != null 
                                  ? `R² ${(m.training_r2 * 100).toFixed(1)}%`
                                  : m.training_mape != null
                                    ? `MAPE ${m.training_mape}%`
                                    : 'Data unavailable'
                                }
                              </p>
                              {m.training_within_10 != null && (
                                <p className="text-slate-500 dark:text-slate-400 text-sm">
                                  {m.training_within_10}% within 10% of sale price
                                </p>
                              )}
                            </div>
                          ) : (
                            // Fallback to list-price-based metrics
                            <p className="text-navy-deep dark:text-white text-2xl sm:text-3xl font-black">
                              {(m.pct_within_20) != null
                                ? `${m.pct_within_20}% within 20%`
                                : 'Data unavailable'
                              }
                            </p>
                          )
                        ) : (
                          <p className="text-navy-deep dark:text-white text-2xl sm:text-3xl font-black">
                            Data unavailable
                          </p>
                        )}
                        <button
                          onClick={() => navigate(`/citystats/${encodeURIComponent(stat.city)}/`)}
                          className="inline-flex items-center gap-1 text-primary font-semibold text-sm hover:underline text-left"
                        >
                          View details
                          <span className="material-symbols-outlined text-base">arrow_forward</span>
                        </button>
                      </div>
                    )
                  })}
                </div>
                <div className="text-center mt-4">
                  <button
                    onClick={() => navigate('/citystats/')}
                    className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-semibold transition-colors"
                  >
                    View all city stats
                    <span className="material-symbols-outlined text-lg">arrow_forward</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* What Drives Our Predictions - house diagram (commented out for now)
      <section className="px-4 sm:px-6 md:px-20 py-12 sm:py-16 bg-gradient-to-b from-slate-50 via-primary/5 to-slate-50 dark:from-slate-950 dark:via-navy-deep/20 dark:to-slate-950">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col gap-8 items-center">
            <div className="text-center max-w-2xl">
              <h2 className="text-navy-deep dark:text-white text-3xl sm:text-4xl font-black mb-3">
                What Drives Our Predictions
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-lg">
                Our models weigh location, size, condition, comparables, neighbourhood, and features to deliver accurate market estimates.
              </p>
            </div>
            <div className="relative w-full max-w-2xl mx-auto rounded-xl shadow-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
              <img
                src="/images/house-components-diagram.png"
                alt="How we break down property value: location, size, condition, comparables, neighbourhood, and features with percentage weights"
                className="w-full block rounded-xl"
              />
              <div
                className="absolute inset-0 rounded-xl pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(43, 124, 238, 0.35) 0%, transparent 45%, transparent 55%, rgba(13, 27, 42, 0.2) 100%)'
                }}
                aria-hidden
              />
            </div>
          </div>
        </div>
      </section>
      */}

      {/* Custom Prediction CTA - Premium Feature */}
      <section className="px-4 sm:px-6 md:px-20 py-12 sm:py-16 bg-gradient-to-br from-primary/5 via-slate-50 to-accent-gold/5 dark:from-primary/10 dark:via-slate-950 dark:to-accent-gold/10">
        <div className="max-w-[1200px] mx-auto">
          <div className="relative overflow-hidden bg-white dark:bg-slate-900 rounded-2xl border-2 border-primary/20 shadow-xl">
            {/* Gradient accent */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent-gold to-primary"></div>
            
            <div className="p-8 sm:p-10">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-primary/20 to-accent-gold/20 border border-primary/30">
                      <span className="material-symbols-outlined text-primary text-sm">diamond</span>
                      <span className="text-primary text-xs font-bold uppercase tracking-wider">Premium Feature</span>
                    </span>
                  </div>
                  <h3 className="text-navy-deep dark:text-white text-2xl sm:text-3xl font-black mb-3">
                    Get a Custom AI Prediction
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-lg mb-6 max-w-xl">
                    Enter any property's details and get an instant AI-powered price prediction with comparable sales analysis. Perfect for buyers and sellers.
                  </p>
                  
                  {/* Feature highlights */}
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <span className="material-symbols-outlined text-primary text-lg">query_stats</span>
                      <span>Instant Predictions</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <span className="material-symbols-outlined text-primary text-lg">compare</span>
                      <span>Comparable Sales</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <span className="material-symbols-outlined text-primary text-lg">map</span>
                      <span>Interactive Map</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => navigate('/predict/')}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-white font-bold rounded-xl transition-all shadow-lg shadow-primary/20"
                  >
                    <span className="material-symbols-outlined">query_stats</span>
                    <span>Try Custom Prediction</span>
                    <span className="material-symbols-outlined">arrow_forward</span>
                  </button>
                </div>
                
                {/* Visual */}
                <div className="hidden lg:flex flex-shrink-0 w-64 h-64 items-center justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent-gold/20 rounded-full blur-3xl"></div>
                    <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-6 border border-slate-200 dark:border-slate-700">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="material-symbols-outlined text-3xl text-primary">home</span>
                        <div>
                          <p className="text-xs text-slate-500 dark:text-slate-400">Your Property</p>
                          <p className="text-sm font-bold text-navy-deep dark:text-white">123 Main St</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500 dark:text-slate-400">AI Prediction</span>
                          <span className="font-bold text-primary">$875,000</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500 dark:text-slate-400">Confidence</span>
                          <span className="font-bold text-emerald-500">±3.2%</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500 dark:text-slate-400">Adjusted Comps</span>
                          <span className="font-bold text-navy-deep dark:text-white">5</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
